import { Component, OnInit, OnDestroy } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { lastValueFrom } from 'rxjs'
import { ProScheApplicationService } from 'src/api/temporary-apis/prosche-application.service'
import { ProScheContractCompanyListResult } from 'src/api/temporary-apis/prosche-application.interface'
import { MessageService } from 'src/app/components/message/message.service'

/**
 * 申込明細情報（行データ + PDF URL）
 */
export interface ProScheApplicationDetailItem {
  rowData: ProScheContractCompanyListResult
  pdfUrl?: SafeResourceUrl
  loading?: boolean
}

@Component({
  selector: 'app-ura-prosche-application-detail-modal',
  templateUrl: './ura-prosche-application-detail-modal.component.html',
  styleUrls: ['./ura-prosche-application-detail-modal.component.scss']
})
export class UraProscheApplicationDetailModalComponent implements OnInit, OnDestroy {
  // モーダル表示フラグ
  isOpen = false
  // 選択された行データのリスト
  selectedRows: ProScheContractCompanyListResult[] = []
  // 現在表示中のインデックス
  currentIndex = 0
  // 明細情報のリスト（PDF URLを含む）
  detailItems: ProScheApplicationDetailItem[] = []
  // PDF URLの簡易キャッシュ（stockMigrationUserAndStatusId -> SafeResourceUrl）
  private pdfUrlCache: Map<string, SafeResourceUrl> = new Map()

  constructor(
    private proScheApplicationService: ProScheApplicationService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    // キャッシュをクリア
    this.pdfUrlCache.clear()
  }

  /**
   * モーダルを開く
   * @param rows 選択された行データのリスト
   */
  open(rows: ProScheContractCompanyListResult[]): void {
    if (!rows || rows.length === 0) {
      this.messageService.error('選択された行がありません')
      return
    }

    this.selectedRows = rows
    this.currentIndex = 0
    this.detailItems = rows.map((row) => ({
      rowData: row,
      loading: false
    }))
    this.isOpen = true

    // 最初の明細PDFを読み込む
    this.loadEstimatePdf(this.currentIndex)
  }

  /**
   * モーダルを閉じる
   */
  close(): void {
    this.isOpen = false
    this.selectedRows = []
    this.currentIndex = 0
    this.detailItems = []
  }

  /**
   * 前の明細に移動
   */
  previous(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--
      this.loadEstimatePdf(this.currentIndex)
    }
  }

  /**
   * 次の明細に移動
   */
  next(): void {
    if (this.currentIndex < this.detailItems.length - 1) {
      this.currentIndex++
      this.loadEstimatePdf(this.currentIndex)
    }
  }

  /**
   * 前へボタンが有効かどうか
   */
  get canGoPrevious(): boolean {
    return this.currentIndex > 0
  }

  /**
   * 次へボタンが有効かどうか
   */
  get canGoNext(): boolean {
    return this.currentIndex < this.detailItems.length - 1
  }

  /**
   * 複数選択かどうか
   */
  get hasMultipleItems(): boolean {
    return this.selectedRows.length > 1 || this.detailItems.length > 1
  }

  /**
   * 現在の件数表示（例：1/3）
   */
  get currentPageInfo(): string {
    const total = this.selectedRows.length > 0 ? this.selectedRows.length : this.detailItems.length
    if (total === 0) {
      return ''
    }
    return `${this.currentIndex + 1} / ${total}`
  }

  /**
   * 現在表示中の明細情報
   */
  get currentDetailItem(): ProScheApplicationDetailItem | null {
    if (this.detailItems.length === 0 || this.currentIndex < 0 || this.currentIndex >= this.detailItems.length) {
      return null
    }
    return this.detailItems[this.currentIndex]
  }

  /**
   * 明細PDFを取得して表示する
   * @param index 表示するインデックス
   */
  private async loadEstimatePdf(index: number): Promise<void> {
    if (index < 0 || index >= this.detailItems.length) {
      return
    }

    const detailItem = this.detailItems[index]
    const stockMigrationUserAndStatusId = detailItem.rowData.stockMigrationUserAndStatusId

    // キャッシュに存在する場合はそれを使用
    if (this.pdfUrlCache.has(stockMigrationUserAndStatusId)) {
      detailItem.pdfUrl = this.pdfUrlCache.get(stockMigrationUserAndStatusId)
      detailItem.loading = false
      return
    }

    // ローディング開始
    detailItem.loading = true

    try {
      const ret = await lastValueFrom(
        this.proScheApplicationService.getProScheApplicationsEstimate(stockMigrationUserAndStatusId)
      )

      if (ret?.resultCode !== 0) {
        this.messageService.error('御見積書確認URLの取得に失敗しました')
        detailItem.loading = false
        return
      }

      const resData = ret.data[0]
      if (!resData || !resData.defaultPdfUrl) {
        this.messageService.error('御見積書確認URLが見つかりません')
        detailItem.loading = false
        return
      }

      // protocol無しで取得されるので、https://を追加
      const pdfUrl = `https:${resData.defaultPdfUrl}`
      const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl)

      // キャッシュに保存
      this.pdfUrlCache.set(stockMigrationUserAndStatusId, safeUrl)
      detailItem.pdfUrl = safeUrl
      detailItem.loading = false
    } catch (error) {
      this.messageService.error('御見積書確認URLの取得中にエラーが発生しました')
      detailItem.loading = false
    }
  }
}
